import { useCallback, useEffect } from 'react';

import noop from 'lodash/noop';

import { useCachedScript } from './useCachedScript';

export type AppleOAuthProps = {
  locale: string;
  onSuccess: (data: AppleOAuthSuccessResponse) => void;
  onError: (error: unknown | AppleOAuthErrorResponse) => void;
};

export type AppleOAuthErrorResponse = {
  error: string | 'popup_closed_by_user';
};

export type AppleOAuthSuccessResponse = {
  authorization: {
    code: string;
    id_token: string;
    state: string;
  };
  user: {
    email: string;
    name: {
      firstName: string;
      lastName: string;
    };
  };
};

export const isAppleErrorResponse = (
  data: AppleOAuthErrorResponse | AppleOAuthSuccessResponse | unknown
): data is AppleOAuthErrorResponse => {
  return data !== null && typeof data === 'object' && data.hasOwnProperty('error');
};

const CLIENT_ID = 'com.onetwotrip.signinwithapple';

export const useAppleLogin = (props: AppleOAuthProps) => {
  const { onSuccess, onError = noop, locale } = props;

  const { loaded, error } = useCachedScript(
    `https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/${locale}/appleid.auth.js`
  );

  const login = useCallback(async () => {
    try {
      const data = (await (window as any).AppleID.auth.signIn()) as
        | AppleOAuthErrorResponse
        | AppleOAuthSuccessResponse;

      if (isAppleErrorResponse(data)) {
        onError(data.error);
      } else {
        onSuccess(data);
      }
    } catch (err) {
      onError(err);
    }
  }, []);

  useEffect(() => {
    if (loaded && !error) {
      try {
        (window as any).AppleID.auth.init({
          clientId: CLIENT_ID,
          locale,
          redirectURI: `${window.origin}/_auth/authenticate/oauth-apple`,
          usePopup: true,
        });
      } catch {
        // ignore
      }
    }
  }, [loaded, error]);

  return { login, loaded, error };
};
